import { ModalLayer, showTileViewResults } from "#skyward_modalLayer";
import { registerEvents } from "./skyward.ui.events";

const skyward = window.skyward;

const classNames =
{
    htmlComboBox: "htmlComboBox",
    uiDisabled: "ui-disabled",
    templatedResult: "templatedResult",
    selected: "selected",
};

const events =
{
    register
};

registerEvents("htmlComboBox", events);

export function populateBoxes(comboBox: JQuery<HTMLElement>, value?: string | number | null)
{
    const valueHolder = comboBox.find(classNames.htmlComboBox.dot() + "_value");
    const input = comboBox.find(classNames.htmlComboBox.dot() + '_input');
    const selectedTile = comboBox.find(`${classNames.templatedResult.dot()}[data-value="${value}"]`);

    const selectedItemHTML = selectedTile.html();
    const li = $("<li>",
        {
            class: classNames.templatedResult,
            tabIndex: -1,
            dataValue: selectedTile.data("value"),
            html: selectedItemHTML
        }
    );

    const listItemClass = $(classNames.htmlComboBox.dot()).data("list-item-class");
    if (listItemClass)
    {
        li.addClass(listItemClass)
    }

    // clear existing selection
    input.empty();
    input.append(li);

    const selectedItemClass = $(classNames.htmlComboBox.dot()).data("selected-item-class");
    if (selectedItemClass)
    {
        input.find(classNames.templatedResult.dot()).addClass(selectedItemClass);
    }

    if (!value)
    {
        value = "";
    }

    valueHolder.val(value);
    valueHolder.trigger("change");
}

function register(comboBox: JQuery<HTMLElement>)
{
    const button = comboBox.find(classNames.htmlComboBox.dot() + '_button');
    const selectValue = comboBox.find(classNames.htmlComboBox.dot() + '_value');
    const input = comboBox.find(classNames.htmlComboBox.dot() + '_input');
    const hiddenSelectionList = comboBox.find(classNames.htmlComboBox.dot() + '_hiddenSelectionList');
    let selectedID = selectValue.val() as string | number | undefined;
    let popupContent: JQuery | null = null;
    let layer: ModalLayer | null = null;

    const label = comboBox.findLabel();
    if (label.length > 0)
    {
        // Add aria-labelledby because the container isn't a standard input control.
        // This is best practice for accessibility in this situation.
        comboBox.attr("aria-labelledby", label.addIds().attr("id")!);
        label.on("click", () => { comboBox.trigger("focus"); })
    }

    comboBox.on("click", function ()
    {
        if (!button.is(classNames.uiDisabled.dot()))
        {
            togglePopup();
        }
    });
    comboBox.on("keydown", function (event: JQuery.KeyboardEventBase)
    {
        const keys = skyward.keys.getKeyNamesFromEvent(event);
        if (!button.is(classNames.uiDisabled.dot()) && (keys.ARROW_UP || keys.ARROW_DOWN || keys.ENTER))
        {
            togglePopup();
        }
    });

    function togglePopup()
    {
        if (layer)
        {
            layer.close();
            return;
        }

        comboBox.attr("aria-expanded", "true");

        const beforeClose = function ()
        {
            button.trigger("focus");
            layer = null;
            comboBox.attr("aria-expanded", "false");
        };

        popupContent = $("<div>", { id: input.attr("id") + "_popup", 'class': "verticalScroll anchored noAnchoredTail popup" });
        popupContent.append(hiddenSelectionList.children().clone(true));

        popupContent.on("click enter", classNames.templatedResult.dot(), function (this: HTMLElement, event)
        {
            const tile = $(this);
            const anchor = tile.find("a").first();
            if (anchor.length && anchor.hasClass(classNames.uiDisabled.dot()))
            {
                return;
            }
            if (event.type === "keydown")
            {
                if (anchor.length) 
                {
                    anchor.trigger("click");
                    return;
                }
            }
            selectedID = tile.data().value;
            populateBoxes(comboBox, selectedID);
            layer!.close();
            comboBox.trigger("focus"); // Need to move focus onto the combobox to prevent the layer from reopening.  
        });

        popupContent.on("keydown", classNames.templatedResult.dot(), keydownEvent);

        layer = showTileViewResults(popupContent, comboBox, beforeClose);

        layer.options.resizeCallback!();

        if (!selectedID)
        {
            selectItem(popupContent.find(classNames.templatedResult.dot()).first());
        }
        else
        {
            selectItem(popupContent.find("[data-value='" + selectValue.val() + "']"));
        }
    }

    function keydownEvent(event: JQuery.KeyboardEventBase)
    {
        if (!$.contains(document.body, event.target))
        {
            return;
        }
        const keys = skyward.keys.getKeyNamesFromEvent(event);
        let nextRow: JQuery | undefined;
        if (!keys.ARROW_LEFT && !keys.ARROW_UP && !keys.ARROW_RIGHT && !keys.ARROW_DOWN && !keys.TAB && !keys.ENTER)
        {
            return;
        }

        if (keys.ARROW_RIGHT || keys.ARROW_DOWN || keys.ENTER) // Right arrow, down arrow and enter perform a search
        {
            if (layer == null || !layer.onscreen)
            {
                togglePopup();
                layer?.close();
            }
        }

        const selectedRow = layer?.contents.find(`${classNames.templatedResult.dot()}${classNames.selected.dot()}`) ?? $();
        if (selectedRow.length === 0)
        {
            return;
        }

        if (keys.ARROW_LEFT || keys.ARROW_UP || keys.ARROW_RIGHT || keys.ARROW_DOWN || keys.TAB) // Change selected record
        {
            if (keys.ARROW_LEFT || keys.ARROW_UP || (keys.TAB && keys.SHIFT)) // left, up
            {
                nextRow = selectedRow.prev();
                if (!nextRow.length)
                {
                    nextRow = layer?.contents.find(classNames.templatedResult.dot()).last() ?? $();
                }
            }
            else // right, down
            {
                nextRow = selectedRow.next();
                if (!nextRow.length)
                {
                    nextRow = layer?.contents.find(classNames.templatedResult.dot()).first() ?? $();
                }
            }

            updateSelectedRow();

            event.preventDefault();
        }

        function updateSelectedRow()
        {
            if (nextRow == null || nextRow.length < 1 || popupContent == null)
            {
                return;
            }

            selectedRow.removeClass(classNames.selected);
            selectedRow.removeAttr("aria-selected");
            nextRow.addClass(classNames.selected);
            nextRow.attr("aria-selected", "true");
            nextRow.trigger("focus");

            const scrollDown = popupContent.height()! < (nextRow.position().top + nextRow.height()!);
            const scrollUp = nextRow.position().top < 0;
            if (scrollDown)
            {
                popupContent.scrollTop(popupContent.scrollTop()! + nextRow.height()!);
            }
            else if (scrollUp)
            {
                popupContent.scrollTop(popupContent.scrollTop()! + nextRow.position().top);
            }
        }
    }

    function selectItem(tile: JQuery)
    {
        tile.addClass(classNames.selected);
        tile.attr("aria-selected", "true");
        tile.trigger("focus");
    }
}